import { Loader2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

/**
 * Register-interest dialog — for visitors who aren't ready to pay through the
 * plan picker but want to be contacted when the next Academy cohort opens.
 * Submitting posts to POST /api/academy/interest via `onSubmit(event)` provided
 * by the parent.
 */
export function AcademyInterestDialog({
    open,
    onOpenChange,
    email,
    onEmailChange,
    interestType,
    onInterestTypeChange,
    note,
    onNoteChange,
    onSubmit,
    submitting,
    submitted,
}) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="bg-weave-bg border-weave-ink max-w-lg"
                data-testid="academy-interest-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="text-2xl font-light text-weave-ink">
                        Hold my place
                    </DialogTitle>
                    <DialogDescription className="text-weave-slate">
                        We'll reach out when the next cohort forms. No obligation —
                        no auto-charge. Just a gentle heads-up.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={onSubmit} className="space-y-4 mt-2">
                    <div>
                        <label className="editorial-label text-weave-slate mb-2 block">
                            Email
                        </label>
                        <Input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => onEmailChange(e.target.value)}
                            placeholder="you@company.com"
                            className="bg-weave-surface border-weave-border focus:border-weave-ink"
                            data-testid="academy-interest-email"
                        />
                    </div>
                    <div>
                        <label className="editorial-label text-weave-slate mb-2 block">
                            Which part are you most drawn to?
                        </label>
                        <div className="grid gap-2">
                            {[
                                { v: "full_academy", label: "The full 12-month Academy" },
                                { v: "personal_clarity_only", label: "Personal Clarity — the first 10 weeks" },
                                { v: "undecided", label: "Not sure yet — let's talk" },
                            ].map((opt) => (
                                <label
                                    key={opt.v}
                                    className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${
                                        interestType === opt.v
                                            ? "border-weave-ink bg-weave-surface-alt"
                                            : "border-weave-border bg-weave-surface hover:border-weave-ink"
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="interest_type"
                                        value={opt.v}
                                        checked={interestType === opt.v}
                                        onChange={() => onInterestTypeChange(opt.v)}
                                        className="accent-weave-teal"
                                        data-testid={`academy-interest-type-${opt.v}`}
                                    />
                                    <span className="text-sm text-weave-ink">
                                        {opt.label}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="editorial-label text-weave-slate mb-2 block">
                            Anything you'd like us to know? (optional)
                        </label>
                        <Textarea
                            value={note}
                            onChange={(e) => onNoteChange(e.target.value)}
                            placeholder="Where you are in your thinking, what would make it right for you…"
                            rows={3}
                            className="bg-weave-surface border-weave-border focus:border-weave-ink"
                            data-testid="academy-interest-note"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={submitting || submitted}
                        className="btn-primary w-full justify-center py-3"
                        data-testid="academy-interest-submit"
                    >
                        {submitting ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Adding you…
                            </>
                        ) : submitted ? (
                            "You're on the list"
                        ) : (
                            <>
                                <Send className="w-4 h-4" />
                                Hold my place
                            </>
                        )}
                    </button>
                </form>
            </DialogContent>
        </Dialog>
    );
}
